import ApiBase from './base/ApiBase';
import {
    ProducerClient,
    ProducerClientCompanyClaim,
    ProducerClientInvite,
    ProducerCreateClientCommand,
    ProducerCreateInvitation,
    ProducerDashboardSummary,
    ProducerFinancialHistory,
    ProducerInvitationClaim,
    ProducerMembershipState,
    ProducerPayoutRun,
    ProducerPayoutRunCandidate,
    ProducerPayoutRunPreview,
    ProducerPayoutSetupSession,
    ProducerPayoutRunExecutionPayload,
    ProducerPayoutRunStartPayload,
    ProducerPayoutStatus,
    ProducerCompanyRateCommand,
    ProducerSystemAdminCompanyCommand,
    ProducerAdminCompany,
    ProducerTeamInvitation,
    ProducerTeamInviteCommand,
    ProducerTeamMember,
} from '../models/producer';

class ProducerPortalClient extends ApiBase {
    private controllerName = 'ProducerPortal';

    async GetDashboard(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerDashboardSummary> {
        return this.GetAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Dashboard`, signal);
    }

    async GetClients(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerClient[]> {
        return this.GetAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Clients`, signal);
    }

    async CreateClientCompany(producerCompanyId: string, context: ProducerCreateClientCommand, signal?: AbortSignal): Promise<ProducerClient> {
        return this.PostAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Clients`, context, signal);
    }

    async GetClientInvitations(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerClientInvite[]> {
        return this.GetAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/ClientInvitations`, signal);
    }

    async CancelClientInvitation(invitationId: string, signal?: AbortSignal): Promise<void> {
        return this.DeleteAsync(`${this.controllerName}/Invitations/${invitationId}`, signal);
    }

    async ResendClientInvitation(invitationId: string, signal?: AbortSignal): Promise<void> {
        return this.PostAsync(`${this.controllerName}/Invitations/${invitationId}/Resend`, null, signal);
    }

    async GetTeamMembers(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerTeamMember[]> {
        return this.GetAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Team`, signal);
    }

    async GetTeamInvitations(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerTeamInvitation[]> {
        return this.GetAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Team/Invitations`, signal);
    }

    async InviteTeamMember(producerCompanyId: string, context: ProducerTeamInviteCommand, signal?: AbortSignal): Promise<void> {
        return this.PostAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Team/Invitations`, context, signal);
    }

    async RevokeTeamInvitation(invitationId: string, signal?: AbortSignal): Promise<void> {
        return this.DeleteAsync(`${this.controllerName}/Invitations/${invitationId}`, signal);
    }

    async ResendTeamInvitation(invitationId: string, signal?: AbortSignal): Promise<void> {
        return this.PostAsync(`${this.controllerName}/Invitations/${invitationId}/Resend`, null, signal);
    }

    async GetFinancials(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerFinancialHistory> {
        return this.GetAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Financials`, signal);
    }

    async GetPayoutStatus(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerPayoutStatus> {
        return this.GetAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Payouts/Status`, signal);
    }

    async CreatePayoutOnboardingSession(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerPayoutSetupSession> {
        return this.PostAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Payouts/Setup`, null, signal);
    }

    async CreatePayoutDashboardSession(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerPayoutSetupSession> {
        return this.PostAsync(`${this.controllerName}/${encodeURIComponent(producerCompanyId)}/Payouts/Dashboard`, null, signal);
    }

    async GetPayoutRunEligibleProducers(signal?: AbortSignal): Promise<ProducerPayoutRunCandidate[]> {
        return this.GetAsync(`${this.controllerName}/Admin/Payouts/RunCandidates`, signal);
    }

    async GetPayoutRunPreview(
        producerCompanyId: string | null = null,
        signal?: AbortSignal,
    ): Promise<ProducerPayoutRunPreview> {
        if (!producerCompanyId)
            return this.GetAsync(`${this.controllerName}/Admin/Payouts/Preview`, signal);

        return this.GetAsync(`${this.controllerName}/Admin/Payouts/Preview/${encodeURIComponent(producerCompanyId)}`, signal);
    }

    async StartPayoutRun(
        payload: ProducerPayoutRunStartPayload,
        producerCompanyId: string = null,
        signal?: AbortSignal,
    ): Promise<ProducerPayoutRunExecutionPayload> {
        const route = producerCompanyId
            ? `${this.controllerName}/Admin/Payouts/Run/${encodeURIComponent(producerCompanyId)}`
            : `${this.controllerName}/Admin/Payouts/Run`;
        const additionalHeaders: [string, string][] = payload.IdempotencyKey
            ? [['Idempotency-Key', payload.IdempotencyKey]]
            : [];
        return this.PostAsync(route, { PreviewFingerprint: payload.PreviewFingerprint }, signal, 'application/json', additionalHeaders);
    }

    async RetryPayoutRun(runId: string, signal?: AbortSignal): Promise<ProducerPayoutRunExecutionPayload> {
        return this.PostAsync(`${this.controllerName}/Admin/Payouts/Run/${encodeURIComponent(runId)}/Retry`, null, signal);
    }

    async GetPayoutRun(runId: string, signal?: AbortSignal): Promise<ProducerPayoutRun> {
        return this.GetAsync(`${this.controllerName}/Admin/Payouts/Run/${encodeURIComponent(runId)}`, signal);
    }

    async GetPayoutRunHistory(signal?: AbortSignal): Promise<ProducerPayoutRun[]> {
        return this.GetAsync(`${this.controllerName}/Admin/Payouts/History`, signal);
    }

    async AddProducer(context: ProducerSystemAdminCompanyCommand, signal?: AbortSignal): Promise<ProducerClientCompanyClaim> {
        return this.PostAsync(`${this.controllerName}/Admin/Companies`, context, signal);
    }

    async GetProducerCompanies(signal?: AbortSignal): Promise<ProducerAdminCompany[]> {
        return this.GetAsync(`${this.controllerName}/Admin/Companies`, signal);
    }

    async GetProducerCompany(producerCompanyId: string, signal?: AbortSignal): Promise<ProducerAdminCompany> {
        return this.GetAsync(`${this.controllerName}/Admin/Companies/${encodeURIComponent(producerCompanyId)}`, signal);
    }

    async SetProducerCompanyState(producerCompanyId: string, state: ProducerMembershipState, signal?: AbortSignal): Promise<void> {
        return this.PutAsync(`${this.controllerName}/Admin/Companies/${encodeURIComponent(producerCompanyId)}/State`, state, signal);
    }

    async AddProducerRate(producerCompanyId: string, context: ProducerCompanyRateCommand, signal?: AbortSignal): Promise<void> {
        return this.PostAsync(`${this.controllerName}/Admin/Companies/${encodeURIComponent(producerCompanyId)}/Rates`, context, signal);
    }

    async GetProducerInvitation(invitationId: string, token: string, signal?: AbortSignal): Promise<ProducerInvitationClaim> {
        return this.PostPublicAsync(`${this.controllerName}/Invitation/${encodeURIComponent(invitationId)}`, { Token: token }, signal, [], 'no-referrer');
    }

    async CompleteProducerInvitation(context: ProducerCreateInvitation, signal?: AbortSignal): Promise<void> {
        return this.PostPublicAsync(`${this.controllerName}/Invitation/Complete`, context, signal, [], 'no-referrer');
    }
}

export default ProducerPortalClient;
